import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { toast } from 'sonner';
import { useBuildHubBoardModal } from './use-build-hub-board-modal';
import { useDesignBoards } from '~/ui-rocco/hooks/use-design-boards';
import type {
    CreateOrUpdateInspirationMutation,
    RemoveInspirationMutation,
    MigrateInspirationMutation,
} from 'generated/graphql';
import { CREATE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-create';
import { REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-remove';
import { MIGRATE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-migrate';
import type { DocumentAccessMode, DocumentRole } from '../types';

interface CreateBoardData {
    title: string;
    description?: string;
    isPrivate?: boolean; // Legacy support
    accessMode?: DocumentAccessMode;
    accessRole?: DocumentRole;
    futureTeamMemberRole?: DocumentRole | 'no-access';
}

export const useBuildHubBoardInspirationActions = () => {
    const migrateFetcher = useFetcher<MigrateInspirationMutation>();
    const removeFetcher = useFetcher<RemoveInspirationMutation>();
    const createFetcher = useFetcher<CreateOrUpdateInspirationMutation>();

    const {
        markBoardAsLinked,
        revertBoardLinking,
        setBoardLinking,
        clearBoardLinking,
    } = useBuildHubBoardModal();
    const { handleFetchBoards } = useDesignBoards();

    // Refs to prevent re-triggering effects on repeated renders
    const hasLinked = useRef(false);
    const hasCreated = useRef(false);
    const hasUnLinked = useRef(false);
    const linkBoardOnSuccessRef = useRef<(() => void) | undefined>(undefined);
    const createBoardOnSuccessRef = useRef<(() => void) | undefined>(undefined);

    const linkData = migrateFetcher.data?.projectInspirationIdeaBookMigrate;
    const linkBoardToBuildHub = useCallback(
        (boardId: number, buildHubId: number, onSuccess?: () => void) => {
            setBoardLinking(boardId);
            hasLinked.current = false;
            linkBoardOnSuccessRef.current = onSuccess;
            migrateFetcher.submit(
                {
                    id: buildHubId.toString(),
                    designBoardId: boardId.toString(),
                },
                {
                    method: 'post',
                    action: MIGRATE_BUILD_HUB_INSPIRATION_API_ROUTE,
                }
            );
        },
        [migrateFetcher, setBoardLinking]
    );

    const unlinkData = removeFetcher.data?.projectInspirationGroupRemove;
    const unlinkBoardFromBuildHub = useCallback(
        (inspirationGroupId: number, buildHubId: number) => {
            hasUnLinked.current = false;
            removeFetcher.submit(
                {
                    id: buildHubId.toString(),
                    inspirationGroupId: inspirationGroupId.toString(),
                },
                {
                    method: 'post',
                    action: REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE,
                }
            );
        },
        [removeFetcher]
    );

    const createData = createFetcher.data?.projectInspirationGroupUpsert;
    const createAndLinkBoard = useCallback(
        (
            boardData: CreateBoardData,
            buildHubId: number,
            onSuccess?: () => void
        ) => {
            hasCreated.current = false;
            createBoardOnSuccessRef.current = onSuccess;

            // Support both new access control and legacy isPrivate
            const formData: Record<string, string> = {
                id: buildHubId.toString(),
                title: boardData.title,
                description: boardData.description || '',
            };

            // If new access control is provided, use it
            if (boardData.accessMode && boardData.accessRole) {
                formData.accessMode = boardData.accessMode;
                formData.accessRole = boardData.accessRole;
            } else {
                // Fall back to isPrivate for legacy support
                formData.isPrivate = boardData.isPrivate?.toString() || 'false';
            }

            createFetcher.submit(formData, {
                method: 'post',
                action: CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
            });
        },
        [createFetcher]
    );

    useEffect(() => {
        const fetcherCompleted =
            migrateFetcher.state === 'idle' && linkData && !hasLinked.current;

        const isSuccess = fetcherCompleted && 'Source' in linkData;
        const isFailed = fetcherCompleted && !('Source' in linkData);

        if (isSuccess) {
            hasLinked.current = true;
            markBoardAsLinked(linkData.Source!.ID);

            toast.success('Board linked to project', {
                action: { label: 'Dismiss', onClick: () => toast.dismiss() },
            });

            handleFetchBoards();
            linkBoardOnSuccessRef.current?.();
        } else if (isFailed) {
            hasLinked.current = true;
            clearBoardLinking();

            toast.error(
                'Message' in linkData
                    ? linkData.Message
                    : 'Failed to link board to project',
                {
                    action: {
                        label: 'Dismiss',
                        onClick: () => toast.dismiss(),
                    },
                }
            );
        }
    }, [
        migrateFetcher.state,
        linkData,
        markBoardAsLinked,
        handleFetchBoards,
        clearBoardLinking,
        revertBoardLinking,
    ]);

    useEffect(() => {
        const fetcherCompleted =
            createFetcher.state === 'idle' && createData && !hasCreated.current;

        const isSuccess = fetcherCompleted && 'Source' in createData;
        const isFailed = fetcherCompleted && !('Source' in createData);

        if (isSuccess) {
            hasCreated.current = true;

            toast.success('Board created and linked to project', {
                action: { label: 'Dismiss', onClick: () => toast.dismiss() },
            });

            handleFetchBoards();
            createBoardOnSuccessRef.current?.();
        } else if (isFailed) {
            hasCreated.current = true;

            toast.error(
                'Message' in createData
                    ? createData.Message
                    : 'Failed to create board',
                {
                    action: {
                        label: 'Dismiss',
                        onClick: () => toast.dismiss(),
                    },
                }
            );
        }
    }, [createFetcher.state, createData, handleFetchBoards]);

    useEffect(() => {
        const fetcherCompleted =
            removeFetcher.state !== 'submitting' &&
            unlinkData &&
            !hasUnLinked.current;

        const isSuccess =
            fetcherCompleted && unlinkData.__typename === 'InspirationGroup';
        const isFailed =
            fetcherCompleted && unlinkData.__typename !== 'InspirationGroup';

        if (isSuccess) {
            hasUnLinked.current = true;
            toast.success('Board unlinked from project', {
                action: { label: 'Dismiss', onClick: () => toast.dismiss() },
            });

            handleFetchBoards();
        } else if (isFailed) {
            hasUnLinked.current = true;
            toast.error(
                'Message' in unlinkData
                    ? unlinkData.Message
                    : 'Failed to unlink board',
                {
                    action: {
                        label: 'Dismiss',
                        onClick: () => toast.dismiss(),
                    },
                }
            );
        }
    }, [removeFetcher.state, unlinkData, handleFetchBoards]);

    return {
        linkBoardToBuildHub,
        unlinkBoardFromBuildHub,
        createAndLinkBoard,
        isMigrating: migrateFetcher.state !== 'idle',
        isUnlinking: removeFetcher.state !== 'idle',
        isCreating: createFetcher.state !== 'idle',
    };
};
